import React, { useState, useEffect } from 'react';
import { Container, Card, Spinner, Alert, Button, Badge, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiCall from '../shared/apiCall';
import SlidingMenu from '../components/SlidingMenu';
import VersionFooter from '../components/VersionFooter';
import { safeFormatDate } from '../shared/utils';

export default function Inbox() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [sharedPlans, setSharedPlans] = useState([]);
  const [selectedShareId, setSelectedShareId] = useState(searchParams.get('shareId') || '');
  const navigate = useNavigate();

  const limit = 20;

  useEffect(() => {
    // Initialize from URL parameter if present
    const shareIdFromUrl = searchParams.get('shareId');
    if (shareIdFromUrl && shareIdFromUrl !== selectedShareId) {
      setSelectedShareId(shareIdFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchMessages(1);
  }, [selectedShareId]);

  const fetchMessages = async (pageNum) => {
    setLoading(true);
    setError('');

    try {
      let url = `/api/shared?action=GET_SHARED_PLAN_CONTACT_MESSAGES&page=${pageNum}&limit=${limit}`;
      if (selectedShareId) {
        url += `&shareId=${selectedShareId}`;
      }

      const response = await apiCall(url, {}, 'GET');

      if (response.success) {
        setMessages(response.messages || []);
        setPage(pageNum);
        setTotalPages(response.pagination?.totalPages || 0);
        setTotal(response.pagination?.total || 0);
        if (response.sharedPlans) {
          setSharedPlans(response.sharedPlans);
        }
      } else {
        setError(response.message || t('inbox.failedToLoadMessages'));
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(t('inbox.errorLoadingMessages'));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      fetchMessages(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFilterChange = (e) => {
    const newShareId = e.target.value;
    setSelectedShareId(newShareId);
    setPage(1);
    // Update URL parameter
    if (newShareId) {
      setSearchParams({ shareId: newShareId });
    } else {
      setSearchParams({});
    }
  };

  const handlePlanClick = (originalPlanId) => {
    navigate(`/plan-detail/${originalPlanId}`);
  };

  return (
    <div className="inbox-page">
      <SlidingMenu title={t('inbox.title')} />
      
      <Container className="py-4">
        {total > 0 && (
          <div className="d-flex justify-content-end mb-4">
            <Badge bg="secondary">
              {total} {total !== 1 ? t('inbox.messagesPlural') : t('inbox.messages')}
            </Badge>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mb-4" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {/* Filter by Shared Plan */}
        {sharedPlans.length > 0 && (
          <Card className="mb-4 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center gap-3">
                <label className="mb-0 fw-bold">Filter by Plan:</label>
                <Form.Select
                  value={selectedShareId}
                  onChange={handleFilterChange}
                  style={{ width: 'auto', minWidth: '250px' }}
                >
                  <option value="">All Plans</option>
                  {sharedPlans.map((plan) => (
                    <option key={plan.shareId} value={plan.shareId}>
                      {plan.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Card.Body>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </Spinner>
            <p className="mt-2">{t('inbox.loadingMessages')}</p>
          </div>
        ) : messages.length === 0 ? (
          <Card className="text-center py-5">
            <Card.Body>
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '1rem' }}></i>
              <h3 className="mb-3">{t('inbox.noMessagesYet')}</h3>
              <p className="text-muted mb-0">
                {t('inbox.noMessagesText')}
              </p>
            </Card.Body>
          </Card>
        ) : (
          <>
            <div className="mb-4">
              {messages.map((msg) => (
                <Card key={msg.id} className="mb-3 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-2">
                          <h6 className="mb-0 me-2">
                            <i className="bi bi-envelope me-2"></i>
                            {msg.email}
                          </h6>
                        </div>
                        <div className="mb-2">
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none"
                            onClick={() => handlePlanClick(msg.originalPlanId)}
                            style={{ fontSize: '0.9rem' }}
                          >
                            <i className="bi bi-calendar-event me-1"></i>
                            <strong>{msg.planName}</strong>
                          </Button>
                        </div>
                        <small className="text-muted">
                          <i className="bi bi-clock me-1"></i>
                          {safeFormatDate(msg.submittedAt, 'PPP p')}
                        </small>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-top" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.message}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-2 mb-4">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1 || loading}
                >
                  <i className="bi bi-chevron-left"></i>
                </Button>
                
                <div className="d-flex gap-1 align-items-center">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        disabled={loading}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages || loading}
                >
                  <i className="bi bi-chevron-right"></i>
                </Button>
                
                <span className="text-muted ms-2">
                  Page {page} of {totalPages}
                </span>
              </div>
            )}
          </>
        )}
      </Container>
      
      <VersionFooter />
    </div>
  );
}
